import { useState, useEffect, useRef } from "react";
import axios from "../config/axios";

const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const FOLDER_ID = process.env.REACT_APP_GOOGLE_PICKER_FOLDER_ID;

export function useGooglePicker({ onFilePicked } = {}) {
  const [gapiLoaded, setGapiLoaded] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const pickerRef = useRef(null);

  useEffect(() => {
    const loadGoogleApi = () => {
      const script = document.createElement("script");
      script.src = "https://apis.google.com/js/api.js";
      script.onload = () => {
        window.gapi.load("picker", () => {
          setGapiLoaded(true);
        });
      };
      script.onerror = () => {
        setApiError(
          "Failed to load Google API. Check your internet or ad-blocker settings."
        );
      };
      document.body.appendChild(script);
    };

    loadGoogleApi();

    return () => {
      if (pickerRef.current) {
        pickerRef.current.setVisible(false);
        pickerRef.current = null;
      }
      document
        .querySelectorAll('script[src="https://apis.google.com/js/api.js"]')
        .forEach((s) => s.remove());
    };
  }, []);

  const fetchAccessToken = async () => {
    try {
      const response = await axios.get("/google-picker/access-token");
      return response.data.access_token;
    } catch (error) {
      const errorMsg = error?.response?.data?.error;

      if (errorMsg === "NEEDS_USER_REAUTH") {
        const currentUrl = window.location.href;
        window.location.href = `${
          process.env.REACT_APP_BACKEND_URL
        }/google-picker/auth/google?returnTo=${encodeURIComponent(currentUrl)}`;
        return null;
      }

      setApiError("Failed to fetch Google access token");
      return null;
    }
  };

  const createPicker = async (
    containerRef,
    lot_number = null,
    onClose = null
  ) => {
    if (!gapiLoaded) {
      setApiError("Google Drive integration is not ready. Please try again.");
      return;
    }

    setPickerLoading(true);
    setApiError(null);

    try {
      const accessToken = await fetchAccessToken();
      if (!accessToken) {
        setPickerLoading(false);
        return;
      }
      showPicker(accessToken, containerRef, lot_number, onClose);
      setPickerLoading(false);
    } catch (error) {
      setApiError(
        "Failed to initialize file picker. Check your Google API configuration."
      );
      setPickerLoading(false);
    }
  };

  const injectPickerZIndexStyle = () => {
    const styleId = "google-picker-zindex-style";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        .picker-dialog, .picker {
          z-index: 2000 !important;
        }
      `;
      document.head.appendChild(style);
    }
  };

  const showPicker = (
    token,
    containerRef,
    lot_number = null,
    onClose = null
  ) => {
    try {
      injectPickerZIndexStyle();
      const origin = window.location.origin;

      const folderView = new window.google.picker.DocsView()
        .setMimeTypes("application/pdf,application/vnd.google-apps.folder")
        .setIncludeFolders(true)
        .setParent(FOLDER_ID);

      const searchView = new window.google.picker.DocsView()
        .setMimeTypes("application/pdf")
        .setIncludeFolders(true)
        .setEnableDrives(true)
        .setSelectFolderEnabled(false);

      const picker = new window.google.picker.PickerBuilder()
        .addView(folderView)
        .addView(searchView)
        .setOAuthToken(token)
        .setDeveloperKey(API_KEY)
        .setOrigin(origin)
        .setCallback((data) =>
          pickerCallback(data, onFilePicked, lot_number, onClose)
        )
        .setSize(600, 400)
        .build();

      pickerRef.current = picker;

      if (containerRef?.current) {
        containerRef.current.getBoundingClientRect();
        picker.setVisible(true);
      } else {
        picker.setVisible(true);
      }
    } catch (error) {
      setApiError(
        "Failed to show file picker: Invalid token or API configuration. Please re-authenticate."
      );
    }
  };

  const pickerCallback = async (
    data,
    customCallback,
    lot_number = null,
    onClose = null
  ) => {
    if (data.action === window.google.picker.Action.PICKED) {
      const file = data.docs[0];
      const fileData = {
        id: file.id,
        name: file.name,
        url: file.url,
        mimeType: file.mimeType,
        createdTime: file.lastEditedUtc,
        lot_number,
      };
      if (customCallback) customCallback(fileData, lot_number);
      if (onClose) onClose();
      return fileData;
    } else if (data.action === window.google.picker.Action.CANCEL) {
      if (onClose) onClose();
    }
    return null;
  };

  return {
    gapiLoaded,
    pickerLoading,
    apiError,
    createPicker,
    pickerCallback,
    setApiError,
  };
}
